import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da25h9jk8r5te5mqshe0";

const contributeSteps = [
  { icon: "ri-share-forward-line", title: "提供线索", desc: "告诉我们作品名称与年代" },
  { icon: "ri-video-upload-line", title: "分享片源", desc: "扫描件、老磁带或旧杂志均可" },
  { icon: "ri-file-search-line", title: "我们考古", desc: "审核、修复、标注来源后上架" },
];

export default function Contribute() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 防垃圾：honeypot 被填则静默当作成功，不提交
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");
    try {
      formData.delete("website_alt");
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.code ||
        responseText ||
        "";
      const ok = res.ok && parsed?.code === "OK" && !/spam/i.test(serverMsg);

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "网络异常，提交未成功。");
    }
  }

  return (
    <section id="contribute" className="scroll-mt-20 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[400px_1fr]">
          <div>
            <p className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-widest text-accent-700">
              <span className="h-px w-6 bg-accent-500"></span>
              投稿补档
            </p>
            <h2 className="text-2xl md:text-4xl">你手里，也许就有一部绝版</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              旧杂志连载、老磁带片源、尘封的扫描件——任何童年记忆的碎片，都欢迎提交。
              我们会尽力考古修复，把它加入补档列表。
            </p>

            <ol className="mt-8 space-y-4">
              {contributeSteps.map((s, i) => (
                <li key={s.title} className="flex items-start gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-sm text-background-50">
                    {i + 1}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{s.title}</span>
                    <span className="block text-xs text-foreground-500">{s.desc}</span>
                  </span>
                </li>
              ))}
            </ol>

            <div className="mt-8 rounded-lg border border-background-200 bg-secondary-100 p-5">
              <p className="text-sm font-medium text-secondary-900">补档小贴士</p>
              <p className="mt-1 text-xs leading-relaxed text-secondary-800">
                提供年代、出品方或原版名称等线索，能显著提高考古命中率。涉及版权方作品时，
                我们仅用于资料保存与怀旧分享。
              </p>
            </div>
          </div>

          <form
            id="contributeForm"
            data-readdy-form="073漫画网投稿补档表单"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
          >
            <h3 className="font-heading text-lg md:text-xl">提交补档请求</h3>
            <p className="mt-1 text-xs text-foreground-500">带 * 为必填项，提交后我们将按顺序处理。</p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="c-name" className="mb-1 block text-sm font-medium text-foreground-800">
                  称呼 *
                </label>
                <input
                  id="c-name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                />
              </div>
              <div>
                <label htmlFor="c-email" className="mb-1 block text-sm font-medium text-foreground-800">
                  邮箱 *
                </label>
                <input
                  id="c-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收处理结果"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                />
              </div>
              <div>
                <label htmlFor="c-comic" className="mb-1 block text-sm font-medium text-foreground-800">
                  作品名称 *
                </label>
                <input
                  id="c-comic"
                  name="comic"
                  type="text"
                  required
                  placeholder="你记忆里的那部作品"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                />
              </div>
              <div>
                <label htmlFor="c-era" className="mb-1 block text-sm font-medium text-foreground-800">
                  播出年代
                </label>
                <select
                  id="c-era"
                  name="era"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                >
                  <option value="">请选择年代</option>
                  <option value="1970年代">1970年代</option>
                  <option value="1980年代">1980年代</option>
                  <option value="1990年代">1990年代</option>
                  <option value="2000年代">2000年代</option>
                  <option value="不确定">不确定</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="c-type" className="mb-1 block text-sm font-medium text-foreground-800">
                  投稿类型
                </label>
                <select
                  id="c-type"
                  name="type"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                >
                  <option value="">请选择类型</option>
                  <option value="片源线索">片源线索</option>
                  <option value="扫描件/画稿">扫描件 / 画稿</option>
                  <option value="旧杂志连载">旧杂志连载</option>
                  <option value="老磁带资料">老磁带资料</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="c-detail" className="mb-1 block text-sm font-medium text-foreground-800">
                  补充说明
                </label>
                <textarea
                  id="c-detail"
                  name="detail"
                  rows={4}
                  maxLength={500}
                  placeholder="年份、出品方、原版名称、你掌握的线索……（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>
            </div>

            {/* 防垃圾隐藏字段 */}
            <div className="website-field" aria-hidden="true">
              <label htmlFor="website-field-input">Website</label>
              <input
                id="website-field-input"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {status === "error" && (
              <p className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-800">
                {formError || "提交失败，请稍后再试。"}
              </p>
            )}
            {status === "success" && (
              <p className="mt-4 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
                已收到你的补档请求！我们会在整理后更新到补档列表。
              </p>
            )}

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    提交补档请求
                  </>
                )}
              </button>
              <span className="text-xs text-foreground-500">
                点击提交即表示你同意用于怀旧资料归档分享。
              </span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}